import { useCallback, useEffect, useRef, useState } from "react";

interface UseInfiniteLoadParams<T> {
  fetchData: (page: number) => Promise<T[]>;
  initialPage?: number;
  pageSize?: number;
}

interface UseInfiniteLoadReturn<T> {
  data: T[];
  isLoading: boolean;
  isInitialLoading: boolean;
  hasMore: boolean;
  error: Error | null;
  observerRef: (node: Element | null) => void;
  reset: () => void;
}

export function useInfiniteLoad<T>({
  fetchData,
  initialPage = 0,
  pageSize = 10,
}: UseInfiniteLoadParams<T>): UseInfiniteLoadReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(initialPage);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const observer = useRef<IntersectionObserver>();

  const observerRef = useCallback(
    (node: Element | null) => {
      if (isLoading) return;

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [isLoading, hasMore]
  );

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const newData = await fetchData(page);
      setData((prev) => [...prev, ...newData]);
      setHasMore(newData.length === pageSize);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch data"));
    } finally {
      setIsLoading(false);
      if (page === initialPage) {
        setIsInitialLoading(false);
        setPage((prev) => prev + 1);
      }
    }
  }, [fetchData, page, pageSize, initialPage]);

  useEffect(() => {
    loadData();
  }, [page]);

  const reset = useCallback(() => {
    setData([]);
    setPage(initialPage);
    setHasMore(true);
    setError(null);
    setIsInitialLoading(true);
  }, [initialPage]);

  return {
    data,
    isLoading,
    isInitialLoading,
    hasMore,
    error,
    observerRef,
    reset,
  };
}
